import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography, FormControl, CircularProgress } from "@mui/material";
import { useCompanyConfigContext } from "@/context/CompanyConfigContext";
import { useAuthContext } from "@/context/AuthContext";
import { AuthLayout } from "@/components/authLayout";
import Input from "@/components/input";
import Button from "@/components/button";
import Link from "@/components/link";
import validateEmail from "@/utils/validateEmail";
import { DEFAULT_COMPANY_CONFIG } from "@/const";
import packageJson from "../../package.json";

export const Login = () => {
  const { user } = useAuthContext();
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const { companyConfig, settings } = useCompanyConfigContext();
  const {
    callLoginUser,
    resultLoginUser,
    loadingLoginUser,
    errorLoginUser,
    setResultLoginUser,
  } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const helpEmail = settings.helpEmail || "";
  const rewardsName = settings.rewardsName || "";
  const emailValid = validateEmail(email);

  if (!helpEmail || !rewardsName) {
    console.error("Help email or rewards name is not set!");
  }

  // Check for error query parameter and remove it immediately
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  useEffect(() => {
    const errorParam = searchParams.get("error");
    if (errorParam === "app-crash") {
      setShowErrorMessage(true);
      // Remove the error parameter from the URL immediately
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("error");
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleHelperText = () => {
    if (!emailValid && email && touched) {
      return "Please enter a valid email address";
    }

    if (errorLoginUser) {
      return "Sorry, we couldn't process your login request. Please try again.";
    }
    return "";
  };

  useEffect(() => {
    if (user?.id && companyConfig && !loadingLoginUser) {
      if (user?.role === "admin" || user?.role === "super-admin") {
        console.log(
          "User is authenticated as admin, redirecting to admin dashboard",
        );
        navigate("/admin");
      } else {
        console.log("User is authenticated, redirecting to shop");
        navigate("/shop");
      }
    }
  }, [user?.id, companyConfig?.id]);

  const submitEmail = async () => {
    if (emailValid) {
      await callLoginUser({ email, returnUrl: window.location.href });
    }
  };

  const onChange = (e: any) => {
    setEmail(e.target.value);
  };

  const onClickHelp = () => {
    window.location.href = `mailto:${helpEmail}?subject=Issue%20logging%20in&body=message%20goes%20here`;
  };

  const renderDevLink = () => {
    if (process.env.NODE_ENV === "development") {
      if (loadingLoginUser) {
        return <div>Loading...</div>;
      }

      if (resultLoginUser?.devLink) {
        return (
          <Link to={resultLoginUser.devLink}>
            Developers! Click here to login!
          </Link>
        );
      }

      return <div style={{ color: "red" }}>Developer login link failed</div>;
    }

    return null;
  };

  return (
    <AuthLayout>
      {resultLoginUser?.status === "success" ? (
        <Box style={{ maxWidth: 480, overflowY: "auto" }}>
          <Typography component="h1" variant="h5">
            Email sent
          </Typography>
          <Box my={2} textAlign="left">
            {<div style={{ textAlign: "center" }}>{renderDevLink()}</div>}
            <Typography variant="body1">
              <strong>Check your inbox.</strong>
            </Typography>

            <Typography variant="body1">
              If the information you entered matches our records, you'll soon
              receive an email containing a one-time sign-in link.
            </Typography>

            <Typography variant="body1" sx={{ marginTop: 3 }}>
              <strong>Email submitted:</strong> {email}
            </Typography>

            <Typography variant="body1">
              Does this email not look right? If not,{" "}
              <Link
                component="span"
                onClick={() => setResultLoginUser(null)}
                style={{ cursor: "pointer" }}
              >
                go back
              </Link>{" "}
              and correct any typos.
            </Typography>

            <Typography variant="body1" sx={{ marginTop: 3 }}>
              <strong>Don't see your email?</strong>
            </Typography>

            <Typography variant="body1">
              Be sure to check/refresh your inbox and check your spam or junk
              folders as well.
            </Typography>
            <Box
              sx={{ marginTop: 4 }}
              my={1}
              width="100%"
              borderBottom={1}
              borderColor="grey.300"
            />
            <Typography
              variant="body1"
              sx={{ marginTop: 4, textAlign: "center" }}
            >
              If you encounter any issues, feel free to reach out to{" "}
              <Link
                component="span"
                onClick={onClickHelp}
                style={{ cursor: "pointer" }}
              >
                {helpEmail}
              </Link>
              .
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box style={{ maxWidth: 480, overflowY: "auto" }}>
          <Typography component="h1" variant="h5">
            Sign in
          </Typography>
          {showErrorMessage && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "error.light",
                borderRadius: 1,
                color: "error.contrastText",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Application Error Detected
              </Typography>
              <Typography variant="body2">
                We encountered an error. Please try again.
              </Typography>
            </Box>
          )}
          <FormControl
            component="form"
            style={{ maxWidth: 350, width: "100%", marginTop: 1 }}
          >
            <Input
              type="email"
              margin="normal"
              required
              id="email"
              name="email"
              label="Email Address"
              autoComplete="email"
              autoFocus
              onChange={onChange}
              onBlur={() => setTouched(true)}
              value={email}
              error={(!emailValid && touched) || errorLoginUser}
              helperText={handleHelperText()}
            />
            <Button
              type="submit"
              onClick={submitEmail}
              disabled={!email || loadingLoginUser}
              sx={{ mt: 3, mb: 2 }}
              data-cypress="login-button"
            >
              {loadingLoginUser ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Sign In With Email"
              )}
            </Button>
          </FormControl>
          <Box style={{ maxWidth: 480 }} sx={{ mt: 1 }}>
            <Box my={1} width="100%" borderTop={1} borderColor="grey.300" />
            <div style={{ fontSize: 18, marginBottom: 8, minHeight: 10 }}>
              {`Welcome to ${rewardsName || DEFAULT_COMPANY_CONFIG.REWARDS_NAME}!`}
            </div>
            <Box my={1} width="100%" borderBottom={1} borderColor="grey.300" />
            <div style={{ textAlign: "left" }}>
              <Typography style={{ marginBottom: 12 }}>
                This site uses passwordless sign-in. Please enter your email and
                a link to access {rewardsName} will be emailed to you. Check
                both your inbox and spam/junkmail folders.
              </Typography>
              <Typography style={{ marginBottom: 12 }}>
                If you are registered and did not receive an email within a
                couple of minutes, please make sure you typed your email
                correctly by trying again.
              </Typography>
              <Typography style={{ marginBottom: 12 }}>
                Still having issues? Email{" "}
                <Link
                  component="span"
                  onClick={onClickHelp}
                  style={{ cursor: "pointer" }}
                >
                  {helpEmail}
                </Link>{" "}
                and provide your first name, last name, email address and the
                name of the company you are registered with. We will reach out
                to you within 24 hours!
              </Typography>
            </div>
            <div style={{ marginTop: 8, color: "grey", textAlign: "center" }}>
              v{packageJson?.version}
            </div>
          </Box>
        </Box>
      )}
    </AuthLayout>
  );
};
